import React from 'react';
import { Helmet } from 'react-helmet-async';
import HeroSplitScreen from '@/components/HeroSplitScreen';
import WhyRotana from '@/components/WhyRotana';
import ServicesSection from '@/components/ServicesSection';
import CorporateServices from '@/components/CorporateServices';
import BusinessCenters from '@/components/BusinessCenters';
import ContactCTA from '@/components/ContactCTA';
import TestimonialsSection from '@/components/TestimonialsSection';
import FAQSection from '@/components/FAQSection';
import { Schema } from '@/components/Schema';

export default function Home() {
  const homepageFAQs = [
    {
      question: "How much does it cost to set up a business in Dubai?",
      answer: "Costs vary by jurisdiction. Mainland setup is typically AED 15k–30k, while Free Zones range from AED 12k–40k depending on the package. Visa fees and office space are additional."
    },
    {
      question: "What are the requirements to start a business in Dubai?",
      answer: "You need a passport, business plan, trade name approval, lease/office space, initial approvals, and a license application. Mainland setups may require a local agent."
    },
    {
      question: "How long does it take to set up a business in UAE?",
      answer: "Free Zone companies typically take 7–15 working days. Mainland companies take 2–4 weeks. Remote formation can expedite the process."
    },
    {
      question: "Can foreigners own 100% of a mainland company in UAE?",
      answer: "Yes. Since 2021, foreigners can own 100% of mainland companies for most business activities without needing a local sponsor."
    },
    {
      question: "What is the difference between free zone and mainland companies?",
      answer: "Free Zones offer 100% ownership and tax benefits but trade is restricted within the zone/internationally. Mainland companies can trade directly anywhere in the UAE."
    },
    {
      question: "Which is better: free zone or mainland in UAE?",
      answer: "Free Zone is best for international trade/holding companies. Mainland is superior if you need to trade directly with UAE consumers or take government contracts."
    },
    {
      question: "What are the benefits of a UAE free zone setup?",
      answer: "100% foreign ownership, 100% capital repatriation, zero personal income tax, no import/export duties, and simplified registration processes."
    },
    {
      question: "Can a free zone company do business on the UAE mainland?",
      answer: "Not directly. You typically need a local distributor or a dual license/mainland authorization if offered by your specific free zone."
    },
    {
      question: "What is a trade license in Dubai?",
      answer: "It is the document authorizing you to conduct commercial activities (buying/selling/importing) issued by the DED or Free Zone authorities."
    },
    {
      question: "What is an investor visa in Dubai?",
      answer: "A 2–3 year renewable residence visa for business owners/partners who invest capital into a UAE company."
    },
    {
      question: "What is the Golden Visa for business owners?",
      answer: "A 5–10 year visa for investors with AED 2M+ capital in UAE businesses. It offers self-sponsorship and stability."
    },
    {
      question: "What is corporate tax in UAE?",
      answer: "9% tax on profits over AED 375,000 (effective June 2023). Small businesses under this threshold pay 0%."
    },
    {
      question: "Who are the best business setup consultants?",
      answer: "Look for consultants with government accreditation, transparent pricing, and comprehensive services (Visa, PRO, Banking support)."
    },
    {
      question: "Do I need a local sponsor in Dubai?",
      answer: "For most activities, no. Foreigners can own 100% of mainland companies. Strategic sectors (e.g., oil/gas) may still require one."
    },
    {
      question: "What documents are required for setup?",
      answer: "Passports, visas, business plan, NOC (if applicable), proof of address, and sometimes educational certificates for professional licenses."
    }
  ];

  return (
    <main>
      <Helmet>
        <title>Business Setup in UAE | Mainland, Freezone &amp; Offshore | RotanaBiz</title>
        <meta name="description" content="Launch your company in UAE with licensed advisors. Transparent pricing, real office solutions. From trade license to bank account, visas & accounting - all under one roof." />
        <meta name="keywords" content="UAE business setup, Dubai company formation, mainland setup, freezone setup, offshore company, business license UAE" />
        <meta property="og:title" content="Business Setup in UAE | RotanaBiz" />
        <meta property="og:description" content="Launch your company in UAE with licensed advisors and transparent pricing." />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_AE" />
        <link rel="canonical" href="https://rotanabiz.com" />
      </Helmet>

      <Schema
        name="Business Setup in UAE | Mainland, Freezone & Offshore | RotanaBiz"
        description="Launch your company in UAE with licensed advisors. Transparent pricing, real office solutions. From trade license to bank account, visas & accounting - all under one roof."
        url="https://rotanabiz.com"
        faqs={homepageFAQs}
        aggregateRating={{
          ratingValue: 4.9,
          reviewCount: 850
        }}
      />

      {/* Hero Section - Full Viewport Split Screen */}
      <HeroSplitScreen />

      <div className="section-divider" />

      {/* Why Rotana Section */}
      <WhyRotana />

      {/* Services Selection */}
      <ServicesSection />

      <div className="section-divider" />

      {/* Corporate Services & Support */}
      <CorporateServices />

      <div className="section-divider" />

      {/* Business Centers */}
      <BusinessCenters />

      <div className="section-divider" />

      <div className="section-divider" />

      {/* Testimonials (Wall of Love) */}
      <TestimonialsSection />

      <div className="section-divider" />

      {/* FAQs */}
      <FAQSection faqs={homepageFAQs} />

      <div className="section-divider" />

      {/* Final Call to Action */}
      <ContactCTA />

    </main>
  );
}
